import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import JSZip from "jszip";
import {
    ArrowLeft,
    Eye,
    Loader2,
    AlertCircle,
    Upload,
    Star,
    CheckCircle,
    XCircle,
    RotateCcw,
    History,
    Edit3,
    ExternalLink,
    CreditCard,
    Check,
    X,
    Building2,
    Download,
} from "lucide-react";
import toast from "react-hot-toast";
import { API_BASE_URL } from "../../config/api";
import { ImagePreview } from "./ClaimsTable";
import {
    Claim,
    ClaimStatus,
    Vendor,
    VENDOR_OPTIONS,
    formatVendor,
    fmt,
    formatStatus,
    getStatusBadgeClasses,
} from "./types";

// ─── Constants & Helpers ──────────────────────────────────────────────────────

const ALL_STATUSES: ClaimStatus[] = [
    "REQUESTED",
    "ACCEPTED_PICKUP",
    "PRE_QC",
    "IN_SERVICE",
    "POST_QC",
    "DISPATCHED",
    "DELIVERED",
    "REJECTED",
    "CANCELLED",
];

const PHOTO_SLOT_LABELS = [
    "Front View",
    "Back View",
    "Size / Brand Tag",
    "Damage Close-Up",
    "Side View",
    "Additional Angle",
];

const getPhotoSlotLabel = (index: number) => PHOTO_SLOT_LABELS[index] || `Photo ${index + 1}`;

const getRatingLabel = (r: number): string => {
    switch (r) {
        case 1:
            return "Poor";
        case 2:
            return "Fair";
        case 3:
            return "Good";
        case 4:
            return "Very Good";
        case 5:
            return "Excellent";
        default:
            return "";
    }
};

const getRatingColor = (r: number): string => {
    if (r >= 4) return "text-green-400";
    if (r === 3) return "text-yellow-400";
    return "text-red-400";
};

// ⭐ NEW: Robust Helper to lookup status across raw URLs, clean filenames & URL-decoded paths
const getFileStatus = (fileUrl: string, statuses: Record<string, string>): string | undefined => {
    if (!statuses || !fileUrl) return undefined;
    if (statuses[fileUrl]) return statuses[fileUrl];

    const rawFname = fileUrl.split("/").pop() || fileUrl;
    if (statuses[rawFname]) return statuses[rawFname];

    const cleanFname = rawFname.split("?")[0];
    if (statuses[cleanFname]) return statuses[cleanFname];

    const decodedFname = decodeURIComponent(cleanFname);
    if (statuses[decodedFname]) return statuses[decodedFname];

    // Fuzzy ending check
    const matchedKey = Object.keys(statuses).find((key) => {
        const cleanKey = key.split("?")[0];
        const decodedKey = decodeURIComponent(cleanKey);
        return (
            cleanKey === cleanFname ||
            decodedKey === decodedFname ||
            cleanFname.endsWith(cleanKey) ||
            cleanKey.endsWith(cleanFname)
        );
    });

    return matchedKey ? statuses[matchedKey] : undefined;
};

// ─── ⭐ Export Images as ZIP Helper ────────────────────────────────────────────
const exportImagesAsZip = async (images: string[], zipName: string, prefix: string, labels?: string[]) => {
    if (!images || images.length === 0) {
        toast.error("No images available to export");
        return;
    }

    const toastId = toast.loading(`Preparing ${images.length} image${images.length > 1 ? "s" : ""}...`);

    try {
        const zip = new JSZip();

        await Promise.all(
            images.map(async (url, idx) => {
                try {
                    const response = await fetch(url);
                    if (!response.ok) throw new Error(`HTTP ${response.status}`);
                    const blob = await response.blob();

                    // Extract extension from URL
                    const urlPath = url.split("?")[0];
                    const ext = urlPath.split(".").pop()?.toLowerCase() || "jpg";

                    // Build a clean file name
                    const label = labels?.[idx] ? `_${labels[idx].replace(/[^a-zA-Z0-9]/g, "_")}` : "";
                    const fileName = `${prefix}_${String(idx + 1).padStart(2, "0")}${label}.${ext}`;

                    zip.file(fileName, blob);
                } catch (err) {
                    console.warn(`Failed to fetch image ${idx + 1}:`, err);
                }
            }),
        );

        const content = await zip.generateAsync({ type: "blob" });

        // Trigger download
        const downloadUrl = URL.createObjectURL(content);
        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = `${zipName}.zip`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(downloadUrl);

        toast.success(`Exported ${images.length} image${images.length > 1 ? "s" : ""}`, { id: toastId });
    } catch (err: any) {
        toast.error(err.message || "Failed to export images", { id: toastId });
    }
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
    <div className="grid grid-cols-[140px_1fr] gap-2 py-2.5 border-b border-[#1f1f1f] last:border-0">
        <span className="text-xs text-gray-500 font-medium uppercase tracking-wide pt-0.5">{label}</span>
        <span className="text-sm text-gray-200 break-words">{value || "—"}</span>
    </div>
);

const SectionCard: React.FC<{
    title: string;
    children: React.ReactNode;
    className?: string;
    icon?: React.ReactNode;
    headerRight?: React.ReactNode;
}> = ({ title, children, className = "", icon, headerRight }) => (
    <div className={`bg-[#0f0f0f] border border-[#2d2d2d] rounded-lg p-5 ${className}`}>
        <div className="flex items-center justify-between mb-3 pb-2 border-b border-[#2d2d2d]">
            <div className="flex items-center gap-2">
                {icon}
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{title}</p>
            </div>
            {headerRight}
        </div>
        {children}
    </div>
);

const StarRatingDisplay: React.FC<{ rating: number }> = ({ rating }) => (
    <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
            <Star
                key={star}
                className={`w-4 h-4 ${star <= rating ? "text-yellow-400 fill-yellow-400" : "text-gray-600"}`}
            />
        ))}
    </div>
);

// ─── Reusable Export Button ──────────────────────────────────────────────────
const ExportImagesButton: React.FC<{
    onClick: () => void;
    disabled?: boolean;
    count: number;
    loading?: boolean;
}> = ({ onClick, disabled, count, loading }) => (
    <button
        onClick={onClick}
        disabled={disabled || count === 0 || loading}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium bg-blue-600/10 border border-blue-500/30 text-blue-400 hover:bg-blue-600/20 hover:text-blue-300 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        title={count === 0 ? "No images to export" : `Download ${count} image${count > 1 ? "s" : ""} as ZIP`}
    >
        {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
        Export ZIP
    </button>
);

const QcImageUpload: React.FC<{
    claimId: number;
    type: "preqc" | "postqc";
    existingImages: string[];
    onUploadComplete: () => void;
    onImageClick: (images: string[], index: number) => void;
}> = ({ claimId, type, existingImages, onUploadComplete, onImageClick }) => {
    const [uploading, setUploading] = useState(false);
    const [deletingUrl, setDeletingUrl] = useState<string | null>(null);
    const [exporting, setExporting] = useState(false);

    const label = type === "preqc" ? "Pre-QC" : "Post-QC";
    const prefix = type === "preqc" ? "PreQC" : "PostQC";

    const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;
        setUploading(true);
        const formData = new FormData();
        Array.from(files).forEach((file) => formData.append("files", file));

        try {
            const endpoint = type === "preqc" ? "preqc-images" : "postqc-images";
            const res = await fetch(`${API_BASE_URL}/api/claim/${claimId}/${endpoint}`, {
                method: "POST",
                body: formData,
            });
            if (!res.ok) throw new Error("Upload failed");
            toast.success(`${label} images uploaded`);
            onUploadComplete();
        } catch (err: any) {
            toast.error(err.message || "Failed to upload images");
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const handleDelete = async (imageUrl: string) => {
        if (!window.confirm("Remove this image?")) return;
        setDeletingUrl(imageUrl);
        try {
            const endpoint = type === "preqc" ? "preqc-images" : "postqc-images";
            const res = await fetch(`${API_BASE_URL}/api/claim/${claimId}/${endpoint}`, {
                method: "DELETE",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ imageUrl }),
            });
            if (!res.ok) throw new Error("Delete failed");
            toast.success("Image removed");
            onUploadComplete();
        } catch (err: any) {
            toast.error(err.message || "Failed to remove image");
        } finally {
            setDeletingUrl(null);
        }
    };

    const handleExport = async () => {
        setExporting(true);
        await exportImagesAsZip(existingImages, `Claim_${claimId}_${prefix}_Images`, prefix);
        setExporting(false);
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {label} Images ({existingImages.length})
                </p>
                <div className="flex items-center gap-2">
                    <ExportImagesButton onClick={handleExport} count={existingImages.length} loading={exporting} />
                    <label className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[#1a1a1a] border border-[#2d2d2d] text-gray-300 hover:bg-[#222222] hover:text-white rounded cursor-pointer transition-colors">
                        {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                        {uploading ? "Uploading..." : "Upload"}
                        <input
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={handleUpload}
                            className="hidden"
                            disabled={uploading}
                        />
                    </label>
                </div>
            </div>

            {existingImages.length > 0 ? (
                <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-4 xl:grid-cols-6 gap-2">
                    {existingImages.map((img, idx) => (
                        <div
                            key={idx}
                            className="relative group aspect-square rounded overflow-hidden border border-[#2d2d2d] hover:border-gray-500 transition-colors"
                        >
                            <button
                                type="button"
                                onClick={() => onImageClick(existingImages, idx)}
                                className="w-full h-full"
                            >
                                <img src={img} alt="" className="w-full h-full object-cover" />
                            </button>
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(img);
                                }}
                                disabled={deletingUrl === img}
                                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 hover:bg-red-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-100"
                                title="Remove image"
                            >
                                {deletingUrl === img ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                    <XCircle className="w-3.5 h-3.5" />
                                )}
                            </button>
                        </div>
                    ))}
                </div>
            ) : (
                <p className="text-xs text-gray-600">No images uploaded yet</p>
            )}
        </div>
    );
};

const ConfirmStatusChangeModal: React.FC<{
    isOpen: boolean;
    currentStatus: ClaimStatus | undefined;
    newStatus: ClaimStatus | null;
    statusNote: string;
    onNoteChange: (v: string) => void;
    onConfirm: () => void;
    onCancel: () => void;
    hasRejectedImage: boolean;
    allImagesReviewed: boolean;
    canRejectClaim: boolean;
    hasCostEntered: boolean;
}> = ({
    isOpen,
    currentStatus,
    newStatus,
    statusNote,
    onNoteChange,
    onConfirm,
    onCancel,
    hasRejectedImage,
    allImagesReviewed,
    canRejectClaim,
    hasCostEntered,
}) => {
    if (!isOpen || !newStatus) return null;

    const isRejecting = newStatus === "REJECTED";
    const isDelivering = newStatus === "DELIVERED";
    const cannotDeliver = isDelivering && !hasCostEntered;

    return (
        <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
            onClick={onCancel}
        >
            <div
                className="relative bg-[#111111] border border-[#2d2d2d] rounded-lg shadow-2xl px-8 py-7 flex flex-col items-center gap-5 min-w-[320px] max-w-md w-full mx-4"
                onClick={(e) => e.stopPropagation()}
            >
                <AlertCircle className={`w-8 h-8 ${cannotDeliver ? "text-amber-400" : "text-blue-400"}`} />
                <h3 className="text-base font-semibold text-white text-center">Confirm Status Change</h3>
                <div className="text-center space-y-3 w-full">
                    <p className="text-sm text-gray-400">Are you sure you want to change the status?</p>
                    <div className="flex items-center justify-center gap-2 text-sm flex-wrap">
                        <span className={getStatusBadgeClasses(currentStatus)}>{formatStatus(currentStatus)}</span>
                        <span className="text-gray-500">→</span>
                        <span className={getStatusBadgeClasses(newStatus)}>{formatStatus(newStatus)}</span>
                    </div>

                    {cannotDeliver && (
                        <div className="bg-amber-500/10 border border-amber-500/30 rounded p-3 text-left">
                            <p className="text-amber-400 text-xs font-semibold flex items-center gap-1.5">
                                <span>⚠️ Warning:</span> Cost Not Entered!
                            </p>
                            <p className="text-amber-300/80 text-xs mt-1">
                                Please enter and save the <strong>Internal Costs</strong> before changing status to{" "}
                                <strong>Delivered</strong>.
                            </p>
                        </div>
                    )}

                    {isRejecting && !allImagesReviewed && (
                        <div className="bg-red-500/10 border border-red-500/30 rounded p-3 text-left">
                            <p className="text-red-400 text-xs font-semibold">⚠️ Review all customer images first</p>
                            <p className="text-red-300/80 text-xs mt-1">
                                Mark every photo as <strong>✓ Accept</strong> or <strong>❌ Reject</strong> before
                                setting status to REJECTED.
                            </p>
                        </div>
                    )}

                    {isRejecting && allImagesReviewed && !hasRejectedImage && (
                        <div className="bg-red-500/10 border border-red-500/30 rounded p-3 text-left">
                            <p className="text-red-400 text-xs font-semibold">
                                ⚠️ At least one image must be ❌ Rejected
                            </p>
                            <p className="text-red-300/80 text-xs mt-1">
                                All images are reviewed, but none are rejected. Mark at least one photo as ❌ Rejected
                                to continue.
                            </p>
                        </div>
                    )}

                    <div className="text-left mt-4">
                        <label className="block text-xs text-gray-500 font-medium uppercase tracking-wide mb-1">
                            {isRejecting ? "Rejection Reason (Required) *" : "Internal Note (Optional)"}
                        </label>
                        <textarea
                            value={statusNote}
                            onChange={(e) => onNoteChange(e.target.value)}
                            rows={3}
                            placeholder={
                                isRejecting
                                    ? "Why is this being rejected?"
                                    : "Add an internal note about this status change..."
                            }
                            className={`w-full px-3 py-2 bg-[#1a1a1a] border rounded text-sm text-gray-200 placeholder-gray-600 focus:outline-none transition-colors ${
                                isRejecting
                                    ? "border-red-500/50 focus:border-red-500"
                                    : "border-[#2d2d2d] focus:border-blue-500"
                            }`}
                        />
                    </div>
                </div>
                <div className="flex items-center gap-3 w-full pt-2">
                    <button
                        onClick={onCancel}
                        className="flex-1 px-4 py-2 text-sm font-medium bg-[#1a1a1a] border border-[#2d2d2d] text-gray-400 hover:bg-[#222222] hover:text-white rounded"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={cannotDeliver || (isRejecting && (!statusNote.trim() || !canRejectClaim))}
                        className="flex-1 px-4 py-2 text-sm font-medium bg-blue-600/20 border border-blue-500/40 text-blue-400 hover:bg-blue-600/30 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Confirm
                    </button>
                </div>
            </div>
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════

const ClaimDetailPage: React.FC = () => {
    const { claimId } = useParams<{ claimId: string }>();
    const navigate = useNavigate();

    const [claim, setClaim] = useState<Claim | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [currentStatus, setCurrentStatus] = useState<ClaimStatus | undefined>(undefined);
    const [updatingStatus, setUpdatingStatus] = useState<ClaimStatus | null>(null);
    const [pendingStatus, setPendingStatus] = useState<ClaimStatus | null>(null);
    const [statusNote, setStatusNote] = useState("");

    const [updatingVendor, setUpdatingVendor] = useState(false);

    const [previewImages, setPreviewImages] = useState<string[]>([]);
    const [previewIndex, setPreviewIndex] = useState(0);
    const [previewOpen, setPreviewOpen] = useState(false);

    const [fileStatuses, setFileStatuses] = useState<Record<string, string>>({});
    const [updatingImage, setUpdatingImage] = useState<string | null>(null);

    const [costs, setCosts] = useState({
        serviceCost: "" as string | number,
        pickupCost: "" as string | number,
        dropCost: "" as string | number,
        totalCost: "" as string | number,
    });
    const [savingCosts, setSavingCosts] = useState(false);

    const [statusHistory, setStatusHistory] = useState<any[]>([]);

    const [editingLogId, setEditingLogId] = useState<number | null>(null);
    const [editingLogText, setEditingLogText] = useState("");
    const [savingLogId, setSavingLogId] = useState<number | null>(null);

    // ⭐ Export loading state for customer photos
    const [exportingCustomer, setExportingCustomer] = useState(false);

    // ⭐ Accept All loading state
    const [isAcceptingAll, setIsAcceptingAll] = useState(false);

    const fetchClaim = useCallback(async () => {
        if (!claimId) return;
        try {
            const res = await fetch(`${API_BASE_URL}/api/claim`);
            if (!res.ok) throw new Error("Failed to load claims");
            const allClaims: Claim[] = await res.json();
            const found = allClaims.find((c) => String(c.id) === String(claimId));
            if (!found) throw new Error("Claim not found");

            setClaim(found);
            setCurrentStatus(found.claimStatus);
            setFileStatuses(found.fileStatuses || {});
            setCosts({
                serviceCost: found.serviceCost ?? "",
                pickupCost: found.pickupCost ?? "",
                dropCost: found.dropCost ?? "",
                totalCost: found.totalCost ?? "",
            });

            const histRes = await fetch(`${API_BASE_URL}/api/claim/${claimId}/history`);
            if (histRes.ok) {
                setStatusHistory(await histRes.json());
            }
        } catch (err: any) {
            setError(err.message || "Failed to load claim");
        } finally {
            setLoading(false);
        }
    }, [claimId]);

    useEffect(() => {
        fetchClaim();
    }, [fetchClaim]);

    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                if (pendingStatus) setPendingStatus(null);
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [pendingStatus]);

    const openImagePreview = (images: string[], index: number) => {
        setPreviewImages(images);
        setPreviewIndex(index);
        setPreviewOpen(true);
    };

    const handleStatusButtonClick = (newStatus: ClaimStatus) => {
        if (newStatus === currentStatus || updatingStatus) return;

        // ⭐ Validation - Block status change to ACCEPTED_PICKUP if not all images accepted
        if (currentStatus === "REQUESTED" && newStatus === "ACCEPTED_PICKUP") {
            const totalImages = claim?.files?.length || 0;
            const acceptedCountCheck = claim?.files
                ? claim.files.filter((file) => getFileStatus(file, fileStatuses) === "ACCEPTED").length
                : 0;

            if (totalImages > 0 && acceptedCountCheck !== totalImages) {
                toast.error(
                    "Please click 'Accept All' on customer photos before changing status to Accepted & Pickup.",
                    {
                        icon: "⚠️",
                        id: "status-validation-error",
                        duration: 4000,
                    }
                );
                return; // Block status change
            }
        }

        setStatusNote("");
        setPendingStatus(newStatus);
    };

    const handleConfirmStatusChange = async () => {
        if (!pendingStatus || !claim) return;
        const newStatus = pendingStatus;
        setPendingStatus(null);
        setUpdatingStatus(newStatus);

        try {
            const body: any = { id: claim.id, status: newStatus, note: statusNote };

            const res = await fetch(`${API_BASE_URL}/api/claim/status`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });

            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.message || "Failed to update status");
            }

            setCurrentStatus(newStatus);
            setClaim((prev) => (prev ? { ...prev, claimStatus: newStatus } : prev));
            toast.success(`Status updated to ${formatStatus(newStatus)}`);
            fetchClaim();
        } catch (err: any) {
            toast.error(err.message || "Failed to update status");
        } finally {
            setUpdatingStatus(null);
        }
    };

    const handleVendorChange = async (selectedVendor: string) => {
        if (!claim) return;
        setUpdatingVendor(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/claim/${claim.id}/vendor`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ vendor: selectedVendor || null }),
            });

            if (!res.ok) throw new Error("Failed to assign vendor");

            setClaim((prev) => (prev ? { ...prev, vendor: (selectedVendor as Vendor) || null } : prev));
            toast.success(selectedVendor ? `Vendor updated to ${formatVendor(selectedVendor)}` : "Vendor removed");
        } catch (err: any) {
            toast.error(err.message || "Failed to update vendor");
        } finally {
            setUpdatingVendor(false);
        }
    };

    const handleImageQC = async (fileUrl: string, status: "ACCEPTED" | "REJECTED" | null) => {
        const fname = fileUrl.split("/").pop() || fileUrl;
        const cleanFname = fname.split("?")[0];
        const decodedFname = decodeURIComponent(cleanFname);
        setUpdatingImage(cleanFname);

        const prev = { ...fileStatuses };
        const next = { ...fileStatuses };

        if (status === null) {
            delete next[fileUrl];
            delete next[fname];
            delete next[cleanFname];
            delete next[decodedFname];
        } else {
            next[fileUrl] = status;
            next[fname] = status;
            next[cleanFname] = status;
            next[decodedFname] = status;
        }
        setFileStatuses(next);

        try {
            const res = await fetch(`${API_BASE_URL}/api/claim/${claimId}/image-status`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ fileUrl, status }),
            });
            if (!res.ok) throw new Error("Failed");
            toast.success(status ? `Image marked ${status}` : "Status cleared");
        } catch {
            setFileStatuses(prev);
            toast.error("Failed to update image status");
        } finally {
            setUpdatingImage(null);
        }
    };

    const handleCostChange = (field: keyof typeof costs, value: string) => {
        const numValue = value === "" ? "" : Number(value);
        const newCosts = { ...costs, [field]: numValue };

        if (field !== "totalCost") {
            const s = Number(newCosts.serviceCost) || 0;
            const p = Number(newCosts.pickupCost) || 0;
            const d = Number(newCosts.dropCost) || 0;
            newCosts.totalCost = s + p + d;
        }
        setCosts(newCosts);
    };

    const handleSaveCosts = async () => {
        setSavingCosts(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/claim/${claimId}/costs`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    serviceCost: costs.serviceCost === "" ? null : Number(costs.serviceCost),
                    pickupCost: costs.pickupCost === "" ? null : Number(costs.pickupCost),
                    dropCost: costs.dropCost === "" ? null : Number(costs.dropCost),
                    totalCost: costs.totalCost === "" ? null : Number(costs.totalCost),
                }),
            });
            if (!res.ok) throw new Error("Failed");
            toast.success("Costs saved successfully");
            fetchClaim();
        } catch {
            toast.error("Failed to save costs");
        } finally {
            setSavingCosts(false);
        }
    };

    const handleSaveLogReason = async (logId: number) => {
        setSavingLogId(logId);
        try {
            const res = await fetch(`${API_BASE_URL}/api/claim/history/${logId}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ reason: editingLogText }),
            });
            if (!res.ok) throw new Error("Failed to save note");

            setStatusHistory((prev) =>
                prev.map((log) => (log.id === logId ? { ...log, reason: editingLogText.trim() || null } : log)),
            );
            toast.success("Status note updated");
            setEditingLogId(null);
        } catch (err: any) {
            toast.error(err.message || "Failed to update note");
        } finally {
            setSavingLogId(null);
        }
    };

    // ⭐ Handler for exporting customer photos
    const handleExportCustomerPhotos = async () => {
        if (!claim) return;
        setExportingCustomer(true);
        const labels = claim.files.map((_, idx) => getPhotoSlotLabel(idx));
        await exportImagesAsZip(claim.files, `Claim_${claim.id}_Customer_Photos`, "Customer_Photo", labels);
        setExportingCustomer(false);
    };

    // ⭐ Sequential Accept All images handler
    const handleAcceptAllImages = async () => {
        if (!claim || !claim.files) return;

        // Find files that are not ACCEPTED
        const filesToAccept = claim.files.filter((file) => getFileStatus(file, fileStatuses) !== "ACCEPTED");

        if (filesToAccept.length === 0) {
            toast.success("All images are already accepted");
            return;
        }

        setIsAcceptingAll(true);
        const toastId = toast.loading(`Accepting ${filesToAccept.length} image${filesToAccept.length > 1 ? "s" : ""}...`);

        // Optimistically update UI
        const prevStatuses = { ...fileStatuses };
        const nextStatuses = { ...fileStatuses };
        filesToAccept.forEach((fileUrl) => {
            const fname = fileUrl.split("/").pop() || fileUrl;
            const cleanFname = fname.split("?")[0];
            const decodedFname = decodeURIComponent(cleanFname);

            nextStatuses[fileUrl] = "ACCEPTED";
            nextStatuses[fname] = "ACCEPTED";
            nextStatuses[cleanFname] = "ACCEPTED";
            nextStatuses[decodedFname] = "ACCEPTED";
        });
        setFileStatuses(nextStatuses);

        try {
            // Sequential API calls to prevent DB race conditions
            for (const fileUrl of filesToAccept) {
                const res = await fetch(`${API_BASE_URL}/api/claim/${claimId}/image-status`, {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ fileUrl, status: "ACCEPTED" }),
                });
                if (!res.ok) throw new Error("Failed to update an image status");
            }

            toast.success(`All ${filesToAccept.length} image${filesToAccept.length > 1 ? "s" : ""} accepted successfully!`, { id: toastId });
            fetchClaim();
        } catch (error) {
            setFileStatuses(prevStatuses);
            toast.error("Failed to accept some images. Please try again.", { id: toastId });
            fetchClaim();
        } finally {
            setIsAcceptingAll(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
                <div className="flex items-center gap-3 text-gray-400">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="text-sm">Loading claim details…</span>
                </div>
            </div>
        );
    }

    if (error || !claim) {
        return (
            <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-6">
                <div className="text-center space-y-4">
                    <AlertCircle className="w-10 h-10 text-red-400 mx-auto" />
                    <p className="text-gray-300 text-sm">{error || "Claim not found"}</p>
                    <button
                        onClick={() => navigate("/service-dashboard")}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-[#1a1a1a] border border-[#2d2d2d] text-gray-300 text-sm rounded hover:bg-[#222222]"
                    >
                        <ArrowLeft className="w-4 h-4" /> Back to Dashboard
                    </button>
                </div>
            </div>
        );
    }

    const isActive = !["DELIVERED", "REJECTED", "CANCELLED"].includes(currentStatus || "");
    const pickupAddress = [claim.address1, claim.address2, claim.city, claim.state, claim.zip]
        .filter(Boolean)
        .join(", ");
    const dropAddress = [claim.dropAddress1, claim.dropAddress2, claim.dropCity, claim.dropState, claim.dropZip]
        .filter(Boolean)
        .join(", ");
    const isSameAddress = pickupAddress === dropAddress;
    const hasFiles = claim.files?.length > 0;

    // ⭐ Accurate counters using getFileStatus
    const acceptedCount = claim.files ? claim.files.filter((f) => getFileStatus(f, fileStatuses) === "ACCEPTED").length : 0;
    const rejectedCount = claim.files ? claim.files.filter((f) => getFileStatus(f, fileStatuses) === "REJECTED").length : 0;
    const pendingReviewCount = (claim.files?.length || 0) - acceptedCount - rejectedCount;

    const hasRejectedImage = rejectedCount > 0;
    const allImagesReviewed = (claim.files?.length || 0) > 0 && pendingReviewCount === 0;
    const canRejectClaim = allImagesReviewed && hasRejectedImage;

    const hasCostEntered = Boolean(
        (costs.totalCost && Number(costs.totalCost) > 0) ||
        (claim.totalCost && Number(claim.totalCost) > 0) ||
        (costs.serviceCost && Number(costs.serviceCost) > 0),
    );

    const allImagesAccepted = hasFiles && acceptedCount === (claim.files?.length || 0);

    return (
        <>
            <div className="min-h-screen bg-[#0a0a0a] text-white">
                <div className="sticky top-0 z-30 bg-[#0d0d0d] border-b border-[#2d2d2d]">
                    <div className="flex items-center gap-4 px-4 sm:px-6 py-3">
                        <button
                            onClick={() => navigate("/service-dashboard")}
                            className="flex-shrink-0 inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            <span className="hidden sm:inline">Back to Dashboard</span>
                            <span className="sm:hidden">Back</span>
                        </button>

                        <div className="h-4 w-px bg-[#2d2d2d] flex-shrink-0" />

                        <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                            <div className="min-w-0">
                                <p className="text-[10px] text-gray-500 uppercase tracking-wide">Service Request</p>
                                <p className="text-sm font-semibold text-white truncate">
                                    {claim.id} — {claim.sale?.customerName || claim.name}
                                </p>
                            </div>
                            <span className={getStatusBadgeClasses(currentStatus)}>{formatStatus(currentStatus)}</span>
                            <span
                                className={`hidden sm:inline-flex px-2.5 py-1 rounded text-xs font-medium ${isActive ? "bg-green-500/10 text-green-400 border border-green-500/20" : "bg-gray-500/10 text-gray-400 border border-gray-500/20"}`}
                            >
                                {isActive ? "Active" : "Closed"}
                            </span>
                        </div>
                    </div>

                    {/* TOOLBAR: Update Status + Vendor */}
                    <div className="px-4 sm:px-6 py-2.5 bg-[#090909] border-t border-[#1f1f1f]">
                        <div className="flex items-center gap-3 flex-wrap">
                            <span className="text-xs text-gray-500 font-medium uppercase tracking-wide flex-shrink-0">
                                Update Status:
                            </span>
                            <div className="relative w-56">
                                <select
                                    value={currentStatus || ""}
                                    onChange={(e) => handleStatusButtonClick(e.target.value as ClaimStatus)}
                                    disabled={updatingStatus !== null}
                                    className="w-full appearance-none pl-3 pr-9 py-2 bg-[#1a1a1a] border border-[#2d2d2d] rounded text-sm text-gray-200 focus:outline-none focus:border-blue-500 cursor-pointer disabled:opacity-60"
                                >
                                    {ALL_STATUSES.map((status) => (
                                        <option key={status} value={status}>
                                            {formatStatus(status)}
                                        </option>
                                    ))}
                                </select>
                                <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none">
                                    {updatingStatus ? (
                                        <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
                                    ) : (
                                        <svg
                                            className="w-4 h-4 text-gray-500"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M19 9l-7 7-7-7"
                                            />
                                        </svg>
                                    )}
                                </div>
                            </div>

                            <div className="h-6 w-px bg-[#2d2d2d] mx-1 hidden md:block" />

                            <span className="text-xs text-gray-500 font-medium uppercase tracking-wide flex-shrink-0 flex items-center gap-1.5">
                                <Building2 className="w-3.5 h-3.5" />
                                Vendor:
                            </span>
                            <div className="relative w-64">
                                <select
                                    value={claim?.vendor || ""}
                                    onChange={(e) => handleVendorChange(e.target.value)}
                                    disabled={updatingVendor}
                                    className={`w-full appearance-none pl-3 pr-9 py-2 border rounded text-sm focus:outline-none focus:border-green-500 cursor-pointer disabled:opacity-60 ${
                                        claim?.vendor
                                            ? "bg-green-500/10 border-green-500/40 text-green-400 font-medium"
                                            : "bg-[#1a1a1a] border-[#2d2d2d] text-gray-400"
                                    }`}
                                >
                                    <option value="">Select Vendor ▼</option>
                                    {VENDOR_OPTIONS.map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </select>
                                <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none">
                                    {updatingVendor ? (
                                        <Loader2 className="w-4 h-4 animate-spin text-green-400" />
                                    ) : (
                                        <svg
                                            className={`w-4 h-4 ${claim?.vendor ? "text-green-400" : "text-gray-500"}`}
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M19 9l-7 7-7-7"
                                            />
                                        </svg>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="px-4 sm:px-6 py-6 max-w-[1600px] mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                        {/* LEFT COLUMN */}
                        <div className="space-y-5">
                            {claim.sale && (
                                <SectionCard title="Product & Dashboard Details">
                                    <Row
                                        label="Invoice ID"
                                        value={<span className="font-mono text-blue-400">{claim.sale.uniqueId || "—"}</span>}
                                    />
                                    <Row label="Product Name" value={claim.sale.productName} />
                                    <Row label="Brand" value={claim.sale.brand} />
                                    <Row label="Color" value={claim.sale.color} />
                                    <Row label="Size" value={claim.sale.size} />
                                    <Row label="Customer Name" value={claim.sale.customerName} />
                                    <Row
                                        label="Phone"
                                        value={
                                            claim.sale.phoneNo ? (
                                                <a
                                                    href={`tel:${claim.sale.phoneNo}`}
                                                    className="text-blue-400 hover:underline font-mono"
                                                >
                                                    {claim.sale.phoneNo}
                                                </a>
                                            ) : (
                                                "—"
                                            )
                                        }
                                    />
                                </SectionCard>
                            )}

                            {claim.previousClaim && (
                                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                                    <div className="flex items-start gap-2">
                                        <RotateCcw className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                                        <div className="flex-1">
                                            <p className="text-red-300 font-semibold text-sm">Resubmitted Request</p>
                                            <p className="text-red-200 text-xs mt-1">
                                                Previous Request:{" "}
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        navigate(`/service-dashboard/${claim.previousClaim?.id}`)
                                                    }
                                                    className="font-mono font-bold text-red-400 hover:text-white underline inline-flex items-center gap-1"
                                                >
                                                    {claim.previousClaim.id} <ExternalLink className="w-3 h-3" />
                                                </button>
                                            </p>
                                            {claim.previousClaim.rejectionReason && (
                                                <p className="text-red-200 text-xs mt-1">
                                                    Previous Rejection:{" "}
                                                    <span className="italic">
                                                        "{claim.previousClaim.rejectionReason}"
                                                    </span>
                                                </p>
                                            )}
                                            <p className="text-red-300/70 text-[10px] mt-2">
                                                {fmt(claim.previousClaim.createdAt)}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {claim.credits && (
                                <SectionCard
                                    title="Care+ Credits & Entitlement"
                                    icon={<CreditCard className="w-4 h-4 text-blue-400" />}
                                >
                                    <div className="grid grid-cols-3 gap-2 text-center my-2">
                                        <div className="bg-[#1a1a1a] border border-[#2d2d2d] rounded p-2.5">
                                            <p className="text-[10px] text-gray-500 uppercase font-medium">Total</p>
                                            <p className="text-base font-bold text-white mt-0.5">
                                                {claim.credits.total}
                                            </p>
                                        </div>
                                        <div className="bg-[#1a1a1a] border border-[#2d2d2d] rounded p-2.5">
                                            <p className="text-[10px] text-gray-500 uppercase font-medium">Used</p>
                                            <p className="text-base font-bold text-yellow-400 mt-0.5">
                                                {claim.credits.used}
                                            </p>
                                        </div>
                                        <div className="bg-[#1a1a1a] border border-[#2d2d2d] rounded p-2.5">
                                            <p className="text-[10px] text-gray-500 uppercase font-medium">Remaining</p>
                                            <p className="text-base font-bold text-green-400 mt-0.5">
                                                {claim.credits.remaining}
                                            </p>
                                        </div>
                                    </div>
                                </SectionCard>
                            )}

                            <SectionCard title="Service Request Info">
                                <Row label="Service Request ID" value={<span className="font-mono">{claim.id}</span>} />
                                <Row label="Care+ ID" value={<span className="font-mono">{claim.bookingRef}</span>} />
                                <Row
                                    label="Assigned Vendor"
                                    value={
                                        claim.vendor ? (
                                            <span className="text-green-400 font-medium">
                                                {formatVendor(claim.vendor)}
                                            </span>
                                        ) : (
                                            <span className="text-gray-600 italic">No vendor assigned</span>
                                        )
                                    }
                                />
                                <Row label="Category" value={claim.category} />
                                <Row label="Sub Service" value={claim.subService} />
                                <Row label="Pickup Date" value={fmt(claim.pickupDate)} />
                                <Row label="Created" value={fmt(claim.createdAt)} />
                                {(() => {
                                    const dateToFormat = claim.statusUpdatedAt || claim.updatedAt;
                                    return dateToFormat ? (
                                        <Row label="Last Updated" value={fmt(dateToFormat)} />
                                    ) : null;
                                })()}
                            </SectionCard>

                            <SectionCard title="Internal Costs (₹)">
                                <div className="space-y-3 text-sm">
                                    <div>
                                        <label className="text-xs text-gray-500 uppercase tracking-wide">
                                            Service Cost
                                        </label>
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={costs.serviceCost}
                                            onChange={(e) => handleCostChange("serviceCost", e.target.value)}
                                            placeholder="0.00"
                                            className="w-full mt-1 px-3 py-2 bg-[#1a1a1a] border border-[#2d2d2d] rounded text-sm text-gray-200 focus:outline-none focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500 uppercase tracking-wide">
                                            Pickup Cost
                                        </label>
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={costs.pickupCost}
                                            onChange={(e) => handleCostChange("pickupCost", e.target.value)}
                                            placeholder="0.00"
                                            className="w-full mt-1 px-3 py-2 bg-[#1a1a1a] border border-[#2d2d2d] rounded text-sm text-gray-200 focus:outline-none focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500 uppercase tracking-wide">
                                            Drop Cost
                                        </label>
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={costs.dropCost}
                                            onChange={(e) => handleCostChange("dropCost", e.target.value)}
                                            placeholder="0.00"
                                            className="w-full mt-1 px-3 py-2 bg-[#1a1a1a] border border-[#2d2d2d] rounded text-sm text-gray-200 focus:outline-none focus:border-blue-500"
                                        />
                                    </div>
                                    <div className="border-t border-[#2d2d2d] pt-3">
                                        <label className="text-xs text-yellow-500 uppercase tracking-wide font-bold">
                                            Total Cost
                                        </label>
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={costs.totalCost}
                                            onChange={(e) => handleCostChange("totalCost", e.target.value)}
                                            placeholder="0.00"
                                            className="w-full mt-1 px-3 py-2 bg-yellow-500/10 border border-yellow-500/40 rounded text-sm text-yellow-300 font-bold focus:outline-none focus:border-yellow-500"
                                        />
                                        <p className="text-[10px] text-gray-600 mt-1">Auto-calculated. Editable.</p>
                                    </div>
                                    <button
                                        onClick={handleSaveCosts}
                                        disabled={savingCosts}
                                        className="w-full mt-2 px-4 py-2 bg-blue-600/20 border border-blue-500/40 text-blue-400 hover:bg-blue-600/30 rounded text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                                    >
                                        {savingCosts ? (
                                            <>
                                                <Loader2 className="w-4 h-4 animate-spin" /> Saving...
                                            </>
                                        ) : (
                                            "💾 Save Costs"
                                        )}
                                    </button>
                                </div>
                            </SectionCard>

                            {claim.rating ? (
                                <SectionCard title="Customer Review">
                                    <div className="flex items-center gap-3 mb-3">
                                        <StarRatingDisplay rating={claim.rating} />
                                        <span className={`text-sm font-semibold ${getRatingColor(claim.rating)}`}>
                                            {claim.rating}/5 — {getRatingLabel(claim.rating)}
                                        </span>
                                    </div>
                                    {claim.customerReview ? (
                                        <div className="bg-[#111111] rounded-lg p-3 border border-[#2d2d2d]">
                                            <p className="text-sm text-gray-300 leading-relaxed italic">
                                                "{claim.customerReview}"
                                            </p>
                                        </div>
                                    ) : (
                                        <p className="text-xs text-gray-600">No written review provided</p>
                                    )}
                                </SectionCard>
                            ) : currentStatus === "DELIVERED" ? (
                                <SectionCard title="Customer Review">
                                    <p className="text-xs text-gray-600">No review submitted yet</p>
                                </SectionCard>
                            ) : null}
                        </div>

                        {/* MIDDLE COLUMN */}
                        <div className="space-y-5">
                            {claim.relatedClaims && claim.relatedClaims.length > 0 && (
                                <SectionCard
                                    title={`Previous Service Requests (${claim.relatedClaims.length})`}
                                    icon={<History className="w-4 h-4 text-blue-400" />}
                                >
                                    <div className="space-y-2.5">
                                        {claim.relatedClaims.map((rel) => (
                                            <div
                                                key={rel.id}
                                                onClick={() => navigate(`/service-dashboard/${rel.id}`)}
                                                className="p-3 bg-[#141414] border border-[#2d2d2d] hover:border-blue-500/50 rounded-lg cursor-pointer transition-colors group flex items-center justify-between"
                                            >
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-sm font-mono font-bold text-blue-400 group-hover:underline">
                                                            {rel.id}
                                                        </span>
                                                        <span className={getStatusBadgeClasses(rel.claimStatus)}>
                                                            {formatStatus(rel.claimStatus)}
                                                        </span>
                                                    </div>
                                                    <p className="text-xs text-gray-400 mt-1">
                                                        {rel.category} {rel.subService ? `• ${rel.subService}` : ""}
                                                    </p>
                                                    <p className="text-[10px] text-gray-600 mt-0.5">
                                                        {fmt(rel.createdAt)}
                                                    </p>
                                                </div>
                                                <ExternalLink className="w-4 h-4 text-gray-600 group-hover:text-blue-400 flex-shrink-0" />
                                            </div>
                                        ))}
                                    </div>
                                </SectionCard>
                            )}

                            <SectionCard title="Status History">
                                <div className="space-y-4">
                                    {statusHistory.map((log) => {
                                        const isEditing = editingLogId === log.id;

                                        return (
                                            <div
                                                key={log.id}
                                                className="relative pl-4 border-l-2 border-[#2d2d2d] group"
                                            >
                                                <div className="absolute -left-[5px] top-1.5 w-2 h-2 rounded-full bg-gray-500" />

                                                <div className="flex items-start justify-between pr-2">
                                                    <div>
                                                        <p className="text-xs text-gray-500 mb-0.5">
                                                            {fmt(log.createdAt)}
                                                        </p>
                                                        <p className="text-sm text-gray-200">
                                                            {formatStatus(log.fromStatus)}{" "}
                                                            <span className="text-gray-500 mx-1">→</span>{" "}
                                                            <span className="font-semibold text-white">
                                                                {formatStatus(log.toStatus)}
                                                            </span>
                                                        </p>
                                                    </div>

                                                    {!isEditing && (
                                                        <button
                                                            onClick={() => {
                                                                setEditingLogId(log.id);
                                                                setEditingLogText(log.reason || "");
                                                            }}
                                                            className="opacity-0 group-hover:opacity-100 p-1 text-gray-500 hover:text-blue-400 transition-opacity"
                                                            title="Edit note"
                                                        >
                                                            <Edit3 className="w-3.5 h-3.5" />
                                                        </button>
                                                    )}
                                                </div>

                                                {isEditing ? (
                                                    <div className="mt-2 space-y-2 pr-2">
                                                        <textarea
                                                            value={editingLogText}
                                                            onChange={(e) => {
                                                                setEditingLogText(e.target.value);
                                                                e.target.style.height = "auto";
                                                                e.target.style.height = `${e.target.scrollHeight}px`;
                                                            }}
                                                            ref={(el) => {
                                                                if (el) {
                                                                    el.style.height = "auto";
                                                                    el.style.height = `${el.scrollHeight}px`;
                                                                }
                                                            }}
                                                            className="w-full px-3 py-2 bg-[#1a1a1a] border border-blue-500/50 rounded text-sm text-gray-200 focus:border-blue-500 focus:outline-none resize-none overflow-hidden min-h-[40px]"
                                                            placeholder="Add a note..."
                                                            rows={1}
                                                            autoFocus
                                                        />
                                                        <div className="flex items-center gap-2">
                                                            <button
                                                                onClick={() => handleSaveLogReason(log.id)}
                                                                disabled={savingLogId === log.id}
                                                                className="px-2.5 py-1 text-[10px] font-medium bg-blue-600/20 border border-blue-500/40 text-blue-400 hover:bg-blue-600/30 rounded flex items-center gap-1"
                                                            >
                                                                {savingLogId === log.id ? (
                                                                    <Loader2 className="w-3 h-3 animate-spin" />
                                                                ) : (
                                                                    <Check className="w-3 h-3" />
                                                                )}
                                                                Save
                                                            </button>
                                                            <button
                                                                onClick={() => setEditingLogId(null)}
                                                                disabled={savingLogId === log.id}
                                                                className="px-2.5 py-1 text-[10px] font-medium bg-[#1a1a1a] border border-[#2d2d2d] text-gray-400 hover:text-white rounded flex items-center gap-1"
                                                            >
                                                                <X className="w-3 h-3" />
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    log.reason && (
                                                        <div className="mt-1.5 p-2 bg-[#1a1a1a] rounded border border-[#2d2d2d] text-xs text-gray-300 italic mr-2 whitespace-pre-wrap break-words">
                                                            "{log.reason}"
                                                        </div>
                                                    )
                                                )}
                                            </div>
                                        );
                                    })}
                                    {statusHistory.length === 0 && (
                                        <p className="text-xs text-gray-600">No history available</p>
                                    )}
                                </div>
                            </SectionCard>

                            <SectionCard title="Service Request Form Details">
                                <Row label="Customer Name" value={claim.name} />
                                <Row
                                    label="Phone"
                                    value={
                                        <a
                                            href={`tel:${claim.phone}`}
                                            className="text-blue-400 hover:underline font-mono"
                                        >
                                            {claim.phone}
                                        </a>
                                    }
                                />
                                <Row
                                    label="Email"
                                    value={
                                        <a
                                            href={`mailto:${claim.email}`}
                                            className="text-blue-400 hover:underline break-all"
                                        >
                                            {claim.email}
                                        </a>
                                    }
                                />
                                <Row label="📍 Pickup Address" value={pickupAddress || "—"} />
                                <Row
                                    label="🚚 Drop Address"
                                    value={
                                        isSameAddress ? (
                                            <span className="text-gray-500 italic text-xs">Same as pickup address</span>
                                        ) : (
                                            dropAddress || "—"
                                        )
                                    }
                                />
                            </SectionCard>

                            <SectionCard title="QC Images">
                                <div className="space-y-5">
                                    <QcImageUpload
                                        claimId={claim.id}
                                        type="preqc"
                                        existingImages={claim.preQcImages || []}
                                        onUploadComplete={fetchClaim}
                                        onImageClick={openImagePreview}
                                    />
                                    <div className="border-t border-[#2d2d2d] pt-4">
                                        <QcImageUpload
                                            claimId={claim.id}
                                            type="postqc"
                                            existingImages={claim.postQcImages || []}
                                            onUploadComplete={fetchClaim}
                                            onImageClick={openImagePreview}
                                        />
                                    </div>
                                </div>
                            </SectionCard>
                        </div>

                        {/* RIGHT / MAIN PHOTOS COLUMN */}
                        <div className="lg:col-span-1 xl:col-span-2">
                            <SectionCard
                                title={`Customer Photos (${claim.files?.length || 0}) — QC Review`}
                                className="h-full"
                                headerRight={
                                    <div className="flex items-center gap-2">
                                        {/* ⭐ ACCEPT ALL BUTTON */}
                                        {hasFiles && !allImagesAccepted && (
                                            <button
                                                onClick={handleAcceptAllImages}
                                                disabled={isAcceptingAll || exportingCustomer}
                                                className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium bg-green-600/10 border border-green-500/30 text-green-400 hover:bg-green-600/20 hover:text-green-300 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                                title="Accept all pending images"
                                            >
                                                {isAcceptingAll ? (
                                                    <Loader2 className="w-3 h-3 animate-spin" />
                                                ) : (
                                                    <CheckCircle className="w-3 h-3" />
                                                )}
                                                Accept All
                                            </button>
                                        )}

                                        {/* EXPORT BUTTON */}
                                        <ExportImagesButton
                                            onClick={handleExportCustomerPhotos}
                                            count={claim.files?.length || 0}
                                            loading={exportingCustomer}
                                        />
                                    </div>
                                }
                            >
                                {hasFiles && (
                                    <div className="mb-4 flex items-center gap-3 flex-wrap text-xs">
                                        <span className="px-2.5 py-1 bg-green-500/10 border border-green-500/20 text-green-400 rounded font-medium">
                                            ✓ Accepted: {acceptedCount}
                                        </span>
                                        <span className="px-2.5 py-1 bg-red-500/10 border border-red-500/20 text-red-400 rounded font-medium">
                                            ✗ Rejected: {rejectedCount}
                                        </span>
                                        {pendingReviewCount > 0 && (
                                            <span className="px-2.5 py-1 bg-gray-500/10 border border-gray-500/20 text-gray-400 rounded font-medium">
                                                ⏳ Pending: {pendingReviewCount}
                                            </span>
                                        )}
                                    </div>
                                )}

                                {hasFiles ? (
                                    <div className="grid grid-cols-2 gap-3">
                                        {claim.files.map((file, idx) => {
                                            // ⭐ Use getFileStatus helper for matching
                                            const status = getFileStatus(file, fileStatuses);
                                            const fname = file.split("/").pop() || file;
                                            const cleanFname = fname.split("?")[0];
                                            const isUpdating = updatingImage === cleanFname;
                                            const slotLabel = getPhotoSlotLabel(idx);

                                            return (
                                                <div
                                                    key={idx}
                                                    className={`relative rounded-lg overflow-hidden border-2 transition-all ${
                                                        status === "ACCEPTED"
                                                            ? "border-green-500 bg-green-500/10 ring-2 ring-green-500/30"
                                                            : status === "REJECTED"
                                                              ? "border-red-500 bg-red-500/10 ring-2 ring-red-500/30"
                                                              : "border-[#2d2d2d]"
                                                    }`}
                                                >
                                                    {status && (
                                                        <div
                                                            className={`absolute top-0 left-0 right-0 z-10 py-1 text-center text-[10px] font-bold uppercase tracking-wider ${
                                                                status === "ACCEPTED"
                                                                    ? "bg-green-600 text-white shadow-md"
                                                                    : "bg-red-600 text-white shadow-md"
                                                            }`}
                                                        >
                                                            {status === "ACCEPTED" ? "✓ Accepted" : "✗ Rejected"}
                                                        </div>
                                                    )}

                                                    <div
                                                        className="aspect-[4/3] cursor-pointer group relative"
                                                        onClick={() => openImagePreview(claim.files, idx)}
                                                    >
                                                        <img
                                                            src={file}
                                                            alt={slotLabel}
                                                            className="w-full h-full object-cover"
                                                        />
                                                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                                                            <Eye className="w-6 h-6 text-white" />
                                                        </div>
                                                    </div>

                                                    <div className="absolute top-6 left-2 px-2 py-0.5 bg-black/70 backdrop-blur-sm rounded text-[10px] text-gray-200 font-medium max-w-[75%] truncate z-10">
                                                        {slotLabel}{" "}
                                                        <span className="text-gray-400 ml-1">
                                                            ({idx + 1}/{claim.files.length})
                                                        </span>
                                                    </div>

                                                    <div className="p-2 bg-[#0a0a0a] flex gap-1">
                                                        <button
                                                            onClick={() =>
                                                                handleImageQC(
                                                                    file,
                                                                    status === "ACCEPTED" ? null : "ACCEPTED",
                                                                )
                                                            }
                                                            disabled={isUpdating}
                                                            className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded text-xs font-semibold transition-colors ${
                                                                status === "ACCEPTED"
                                                                    ? "bg-green-600 text-white hover:bg-green-700 shadow-sm"
                                                                    : "bg-[#1a1a1a] text-gray-400 border border-[#2d2d2d] hover:bg-green-600/20 hover:text-green-400"
                                                            } disabled:opacity-50`}
                                                        >
                                                            {isUpdating && status !== "ACCEPTED" ? (
                                                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                            ) : (
                                                                <CheckCircle className="w-3.5 h-3.5" />
                                                            )}
                                                            Accept
                                                        </button>
                                                        <button
                                                            onClick={() =>
                                                                handleImageQC(
                                                                    file,
                                                                    status === "REJECTED" ? null : "REJECTED",
                                                                )
                                                            }
                                                            disabled={isUpdating}
                                                            className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded text-xs font-semibold transition-colors ${
                                                                status === "REJECTED"
                                                                    ? "bg-red-600 text-white hover:bg-red-700 shadow-sm"
                                                                    : "bg-[#1a1a1a] text-gray-400 border border-[#2d2d2d] hover:bg-red-600/20 hover:text-red-400"
                                                            } disabled:opacity-50`}
                                                        >
                                                            {isUpdating && status !== "REJECTED" ? (
                                                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                            ) : (
                                                                <XCircle className="w-3.5 h-3.5" />
                                                            )}
                                                            Reject
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-center h-40 text-gray-600 text-sm">
                                        No photos uploaded by customer
                                    </div>
                                )}

                                {hasFiles && (
                                    <div className="mt-4 p-3.5 bg-[#141414] border border-[#2d2d2d] rounded-lg text-xs space-y-1.5">
                                        <p className="text-gray-300 font-medium flex items-center gap-1.5">
                                            <span>💡</span> <strong>QC Image Review Guide:</strong>
                                        </p>
                                        <ul className="text-gray-400 space-y-1 pl-5 list-disc text-[11px] leading-relaxed">
                                            <li>
                                                Review <strong className="text-gray-200">every</strong> customer photo
                                                and mark it as{" "}
                                                <strong className="text-green-400 font-semibold">✓ Accept</strong> or{" "}
                                                <strong className="text-red-400 font-semibold">❌ Reject</strong>.
                                            </li>
                                            <li>
                                                Use the <strong className="text-green-400">Accept All</strong> button at the top to quickly accept all pending images.
                                            </li>
                                            <li>
                                                Status can be changed to <strong className="text-blue-400">Accepted & Pickup</strong> only after all images are accepted.
                                            </li>
                                            <li>
                                                Status can be set to{" "}
                                                <strong className="text-yellow-400">REJECTED</strong> only after:
                                                <span className="block mt-1 text-gray-500">
                                                    1) All images are reviewed &nbsp;·&nbsp; 2) At least one image is ❌
                                                    Rejected
                                                </span>
                                            </li>
                                            {pendingReviewCount > 0 && (
                                                <li className="text-yellow-400/90">
                                                    <strong>{pendingReviewCount}</strong> image
                                                    {pendingReviewCount > 1 ? "s" : ""} still pending review.
                                                </li>
                                            )}
                                            {allImagesReviewed && !hasRejectedImage && (
                                                <li className="text-yellow-400/90">
                                                    All images reviewed, but none rejected yet. Mark at least one as ❌
                                                    Rejected to allow rejection.
                                                </li>
                                            )}
                                            {canRejectClaim && (
                                                <li className="text-green-400/90">
                                                    Ready — you can now set status to REJECTED.
                                                </li>
                                            )}
                                        </ul>
                                    </div>
                                )}
                            </SectionCard>
                        </div>
                    </div>
                </div>
            </div>

            <ConfirmStatusChangeModal
                isOpen={pendingStatus !== null}
                currentStatus={currentStatus}
                newStatus={pendingStatus}
                statusNote={statusNote}
                onNoteChange={setStatusNote}
                onConfirm={handleConfirmStatusChange}
                onCancel={() => setPendingStatus(null)}
                hasRejectedImage={hasRejectedImage}
                allImagesReviewed={allImagesReviewed}
                canRejectClaim={canRejectClaim}
                hasCostEntered={hasCostEntered}
            />

            {previewOpen && (
                <ImagePreview
                    images={previewImages}
                    index={previewIndex}
                    onClose={() => setPreviewOpen(false)}
                    onNext={() => setPreviewIndex((i) => (i + 1) % previewImages.length)}
                    onPrev={() => setPreviewIndex((i) => (i - 1 + previewImages.length) % previewImages.length)}
                />
            )}
        </>
    );
};

export default ClaimDetailPage;